/**
 * Forecast-accuracy metrics. Pure functions over aligned arrays of forecasts
 * `f`, realised values `a`, and (where needed) the origin value `a0`.
 * Every function returns `null` instead of a number when the inputs do not
 * support the metric, so the UI can print "n/a" rather than a fake 0.
 *
 * Definitions (repeated in the UI tooltips and in docs/forecast-api-v2.md §4).
 */

function pairs(f, a) {
  const out = [];
  const n = Math.min(f ? f.length : 0, a ? a.length : 0);
  const num = (v) => (v === null || v === undefined || v === "" ? NaN : Number(v));
  for (let i = 0; i < n; i++) {
    const x = num(f[i]);
    const y = num(a[i]);
    if (Number.isFinite(x) && Number.isFinite(y)) out.push([x, y]);
  }
  return out;
}

/** √(mean (f−a)²) */
export function rmse(f, a) {
  const p = pairs(f, a);
  if (!p.length) return null;
  return Math.sqrt(p.reduce((s, [x, y]) => s + (x - y) ** 2, 0) / p.length);
}

/** mean |f−a| */
export function mae(f, a) {
  const p = pairs(f, a);
  if (!p.length) return null;
  return p.reduce((s, [x, y]) => s + Math.abs(x - y), 0) / p.length;
}

/**
 * mean |f−a| / |a| × 100. Only meaningful for ratio-scale levels with all
 * |a| > 0; returns null otherwise (never divides by zero).
 */
export function mape(f, a) {
  const p = pairs(f, a);
  if (!p.length || p.some(([, y]) => y === 0)) return null;
  return (p.reduce((s, [x, y]) => s + Math.abs((x - y) / y), 0) / p.length) * 100;
}

/** mean (f−a); positive = the forecast ran high */
export function bias(f, a) {
  const p = pairs(f, a);
  if (!p.length) return null;
  return p.reduce((s, [x, y]) => s + (x - y), 0) / p.length;
}

/**
 * Theil's U against a benchmark path: RMSE(model) / RMSE(benchmark) on the
 * same observations. < 1 means the model beat the benchmark.
 */
export function theilU(f, a, benchmark) {
  const num = rmse(f, a);
  const den = rmse(benchmark, a);
  if (num == null || den == null || den === 0) return null;
  return num / den;
}

/** Theil's U versus the no-change forecast from the origin value `a0`. */
export function theilURandomWalk(f, a, a0) {
  if (a0 === null || a0 === undefined || a0 === "" || !Number.isFinite(Number(a0))) return null;
  return theilU(f, a, a.map(() => Number(a0)));
}

/**
 * Directional hit rate versus the anchor (percent): share of horizons where
 * sign(f_h − a0) equals sign(a_h − a0). Horizons where either side is exactly
 * flat are excluded; null when nothing is left to score.
 */
export function hitRateVsAnchor(f, a, a0) {
  if (a0 === null || a0 === undefined || a0 === "") return null;
  const o = Number(a0);
  if (!Number.isFinite(o)) return null;
  let n = 0;
  let hits = 0;
  for (const [x, y] of pairs(f, a)) {
    const sf = Math.sign(x - o);
    const sa = Math.sign(y - o);
    if (sf === 0 || sa === 0) continue;
    n++;
    if (sf === sa) hits++;
  }
  return n ? (hits / n) * 100 : null;
}

/**
 * Empirical coverage (percent) of an interval: share of realised values with
 * lower ≤ a ≤ upper. Entries with a missing bound or value are skipped.
 */
export function coverage(lower, upper, a) {
  let n = 0;
  let inside = 0;
  const len = Math.min(lower ? lower.length : 0, upper ? upper.length : 0, a ? a.length : 0);
  for (let i = 0; i < len; i++) {
    const miss = (v) => v === null || v === undefined || v === "";
    if (miss(lower[i]) || miss(upper[i]) || miss(a[i])) continue;
    const lo = Number(lower[i]);
    const hi = Number(upper[i]);
    const y = Number(a[i]);
    if (![lo, hi, y].every(Number.isFinite)) continue;
    n++;
    if (y >= lo && y <= hi) inside++;
  }
  return n ? (inside / n) * 100 : null;
}

/**
 * Status of a Theil's U reading for the UI: good (<0.95), similar (0.95–1.05),
 * bad (>1.05), or null. Thresholds are a display convention, documented here.
 */
export function theilStatus(u) {
  if (u == null || !Number.isFinite(u)) return null;
  if (u < 0.95) return "good";
  if (u <= 1.05) return "similar";
  return "bad";
}

/**
 * Coverage verdict against the nominal level: "undercover" when realised
 * coverage is more than 10 points below nominal, "overcover" when more than
 * 10 points above, else "ok". null when coverage is unknown.
 */
export function coverageStatus(nominal, realised) {
  if (realised == null || !Number.isFinite(realised)) return null;
  if (realised < nominal - 10) return "undercover";
  if (realised > nominal + 10) return "overcover";
  return "ok";
}

export const METRIC_HELP = {
  rmse: { label: "RMSE", help: "Root mean squared error: √(mean (forecast − realised)²). Same unit as the series." },
  mae: { label: "MAE", help: "Mean absolute error: mean |forecast − realised|." },
  mape: { label: "MAPE", help: "Mean absolute percentage error: mean |forecast − realised| / |realised| × 100. Shown only for ratio-scale levels with no zero realised value." },
  bias: { label: "Bias", help: "Mean (forecast − realised). Positive means the model ran high." },
  theilURandomWalk: { label: "Theil's U · RW", help: "RMSE(model) / RMSE(no-change forecast from the origin) on the same weeks. Below 1 beats the no-change benchmark." },
  theilUConsensus: { label: "Theil's U · cons.", help: "RMSE(model) / RMSE(market consensus path) on the same weeks. Null when no consensus path exists for the series." },
  hitRate: { label: "Hit rate", help: "Mean directional accuracy versus the anchor: share of horizons where the forecast's direction from the origin matched the realised direction." },
  coverage80: { label: "Cov. 80%", help: "Share of realised values inside the 80% prediction interval. Should be near 80; lower means over-confident intervals." },
  coverage95: { label: "Cov. 95%", help: "Share of realised values inside the 95% prediction interval. Should be near 95." },
  r2: { label: "R²", help: "In-sample share of target variance explained by the regression. Not evidence of forecasting skill." },
  r2Adj: { label: "Adj. R²", help: "R² penalised for the number of regressors." },
  regressionSe: { label: "Reg. SE", help: "Standard error of the regression (residual standard deviation), in the equation's own units." },
  durbinWatson: { label: "Durbin–Watson", help: "Autocorrelation of residuals; ≈2 means none, <1.5 positive autocorrelation." },
  nObs: { label: "n", help: "Number of observations used in estimation." },
};
